import React, { useState, useMemo, useEffect } from "react";
import { DataTableDemoTemplate } from "../../../ui/DataTableAdjusted";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { DownloadIcon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../../ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../ui/alert-dialog";
import { MoreHorizontal } from "lucide-react";
import Loader from "../../../Loader/Loader";
import {getAllProfesoresByStatus} from "../../../../provider/adm/profesores/teachersByStatus";
import { Dialog, DialogContent, DialogTrigger } from "../../../ui/dialog";
import CrearProfesorDialog from "./CrearProfesor";
import { deleteProfesor } from "../../../../provider/adm/profesores/deleteProfesor";
import { changeStatusProfesor } from "../../../../provider/adm/profesores/changeStatus";
import CrearEditarProfesorPersonalInfo from "../CrearEditProfesoresInfoPersonal";
import CrearEditarProfesorBankData from "../CrearEditProfesorBankData";
import { ExcelExporter } from "../../../../Lib/ExcelExporter";

export function DataTableDemo() {
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  //TraerData
  useEffect(() => {
    setLoading(true)
    const fetchStudents = async () => {
      try {
        const data_fromAPI = await getAllProfesoresByStatus(true);
        setData(data_fromAPI);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally{
        setLoading(false)
      }
    };
    fetchStudents();
  }, []);

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredData = useMemo(() => {
    let filtered = data;

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        ["fullName", "email", "id", "username", "phoneNumber", "identificationNumber"].some((key) => {
          const value = item[key];
          return (
            typeof value === "string" &&
            value.toLowerCase().includes(searchTerm.toLowerCase())
          );
        })
      );
    }
    return filtered;
  }, [data, searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ row }) => <div>{row.getValue("id")}</div>,
    },
    {
      accessorKey: "fullName",
      header: "Name",
      cell: ({ row }) => <div>{row.getValue("fullName")}</div>,
    },
    {
      accessorKey: "username",
      header: "user",
      cell: ({ row }) => <div>{row.getValue("username")}</div>,
    },
    {
      accessorKey: "phoneNumber",
      header: "Phone Number",
      cell: ({ row }) => <div>{row.getValue("phoneNumber")}</div>,
    },
    {
      accessorKey: "emergencyContact",
      header: "Emergency Contact",
      cell: ({ row }) => <div>{row.getValue("emergencyContact")}</div>,
    },
    {
      accessorKey: "identificationType",
      header: "Identification Type",
      cell: ({ row }) => <div>{row.getValue("identificationType")}</div>,
    },
    {
      accessorKey: "identificationNumber",
      header: "Identification Number",
      cell: ({ row }) => <div>{row.getValue("identificationNumber")}</div>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div
          className={`flex items-center justify-center p-1 rounded-lg text-white font-semibold ${
            row.original.status ? "bg-green-500" : "bg-red-500"
          }`}
        >
          <span className="w-2 h-2 rounded-full mr-3 bg-white"></span>
          <span>
            {row.original.status ? "Activo" : "Inactivo"}
          </span>
        </div>
      ),
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const profesor = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost">Editar informacion personal</Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-full md:w-1/3 bg-gray-100 text-black min-h-screen p-6 overflow-y-auto"
                  >
                    <SheetHeader>
                      <SheetTitle className="text-xl font-bold mb-4">
                        Editar profesor
                      </SheetTitle>
                      <SheetDescription>
                        Llena los datos para editar profesor{" "}
                        {profesor.fullName}.
                      </SheetDescription>
                    </SheetHeader>
                    <CrearEditarProfesorPersonalInfo
                      data={profesor}
                      context={"edit"}
                    />
                  </SheetContent>
                </Sheet>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost">Editar informacion bancaria</Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-full md:w-1/3 bg-gray-100 text-black min-h-screen p-6 overflow-y-auto"
                  >
                    <SheetHeader>
                      <SheetTitle className="text-xl font-bold mb-4">
                        Editar profesor informacion bancaria
                      </SheetTitle>
                      <SheetDescription>
                        Llena los datos para editar {profesor.fullName}.
                      </SheetDescription>
                    </SheetHeader>
                    <CrearEditarProfesorBankData
                      personal_info_teacher={profesor}
                      context={"edit"}
                    />
                  </SheetContent>
                </Sheet>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async (value) => {
                  setLoading(true);
                  try {
                    await changeStatusProfesor(profesor.id);
                  } catch (error) {
                    console.error("Error updating student:", error);
                  } finally {
                    window.location.reload();
                    setLoading(false);
                  }
                }}
              >
                <Button variant="ghost">Cambiar estado</Button>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <AlertDialog>
                  <AlertDialogTrigger>
                    <Button variant="ghost">Eliminar</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        ¿Estás absolutamente seguro?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta acción no se puede deshacer. Esto eliminará
                        permanentemente al profesor y todos sus datos
                        asociados.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={async (e) => {
                          e.stopPropagation();
                          setLoading(true);
                          try {
                            await deleteProfesor(profesor.id);
                             setTimeout(() => {
                              window.location.reload();
                            }, 2000);
                          } catch (error) {
                            console.error(
                              "Error eliminando al estudiante:",
                              error
                            );
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        Sí, eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const handleExportToExcel = () => {
    const success = ExcelExporter.exportProfesores(filteredData, true);
    if (success) {
      console.log('Excel exportado exitosamente');
    } else {
      console.error('Error al exportar Excel');
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="w-full p-10" style={{ overflowY: "scroll" }}>
        <div className="bg-white rounded-lg flex justify-between items-center p-5">
          <h2 className="text-xl font-bold text-gray-900">
            Lista de profesores activos
          </h2>
          <DownloadIcon 
            className="h-6 w-6 cursor-pointer hover:text-blue-600 transition-colors" 
            onClick={handleExportToExcel}
            title="Exportar a Excel"
          />
        </div>
        
        <div className="flex items-center py-4 justify-between">
          <Input
            placeholder="Search by name, email, ID, username, phone or identification"
            className="w-96"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <Button
            variant="ghost"
            onClick={() => {
              setSearchTerm("");
            }}
          >
            Clear filters
          </Button>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button>Crear nuevo profesor +</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[400px]">
              <CrearProfesorDialog />
            </DialogContent>
          </Dialog>
        </div>
        
        <div className="mb-4 text-sm text-gray-600">
          Mostrando {filteredData.length} profesor(es)
          {filteredData.length !== data.length &&
            ` (filtrados de ${data.length} total)`}
        </div>
        
        <DataTableDemoTemplate
          columns={columns}
          dataToShow={filteredData}
          rowClickToNavigate={"/admin/profesores/dashboard"}
          localstorage_name={"selected_teacher"}
          externalCurrentPage={currentPage}
          onPageChange={handlePageChange}
          itemsPerPage={20}
        />
      </div>
    </>
  );
}
